class NewsletterForm extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'list', 'email', 'error']
  }

  connectedCallback () {
    super.connectedCallback()

    this.defaultProperties = { 'Accepts Marketing': 'true' }
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )

    this.listeners.add(
      this.els.email.element,
      'input',
      this.handleEmailInput.bind(this),
    )
  }

  handleEmailInput () {
    if (this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', false)
    } else {
      this.els.email.element.setAttribute('aria-invalid', true)
    }
  }

  async onFormSubmit (e) {
    e.preventDefault()

    if (!window.theme?.klaviyo) return

    const listId = this?.els?.list?.element?.value
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    if (!this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', true)
      this.els.email.element.focus()
      return
    }

    // Check if profile exists already before submitting.
    const profileExists = await this.checkProfileExists(emailAddress)

    // If profile already exists, skip to register form.
    if (profileExists) {
      this.log('Profile already exists')
      this.toggleErrorMessage(window.theme.strings.newsletterEmailInUse)
      return
    }

    this.createClientSubscription({
      emailAddress,
      listId,
    })
  }

  // Klaviyo API Methods.
  async checkProfileExists (emailAddress) {
    const scriptUrl = 'https://self-portrait-klaviyo.vercel.app/api/identify'

    const data = {
      email: emailAddress,
      store: window.theme?.klaviyo?.store,
    }

    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    const jsonResponse = await response.json()

    return jsonResponse.registered ? jsonResponse.registered : false
  }

  async createClientSubscription (formData) {
    const { emailAddress, listId } = formData
    const properties = { ...this.defaultProperties }

    const attributes = {
      email: emailAddress,
      properties,
    }

    const bodyData = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: window.theme.klaviyo.apiVersion,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${window.theme.klaviyo.apiKey}`,
        options,
      )

      // Expected success code is 202, but the response body is empty.
      // This is expected according to the docs:
      // 'The request has been received but not yet acted upon. We return this status
      //  code for requests that were accepted but are processed asynchronously.'
      if (response.status === 202) {
        this.toggleErrorMessage()
        this.showSubscribedMessage()
      } else {
        // There is data returned when there is an error.
        // On success, the response body is empty.
        const jsonResponse = await response.json()

        if (jsonResponse.errors.length > 0) {
          // eslint-disable-next-line no-console
          const errorMessage = jsonResponse.errors[0].detail
            ? jsonResponse.errors[0].detail
            : window.theme.strings.newsletterGenericError
          this.toggleErrorMessage(errorMessage)
        }
      }
    } catch (error) {
      console.error(error)
      const errorMessage =
        error && error.detail
          ? error.detail
          : window.theme.strings.newsletterGenericError
      this.toggleErrorMessage(errorMessage)
    }
  }

  toggleErrorMessage (message = false) {
    const errorElement = this.els.error.element
    if (!message) {
      errorElement.setAttribute('aria-hidden', true)
    } else {
      errorElement.setAttribute('aria-hidden', false)
      errorElement.innerHTML = message
    }
  }

  showSubscribedMessage () {
    if (this.dataset.redirect) {
      window.location.href = this.dataset.redirect
      return
    }

    window.localStorage.setItem(this.submittedStorageName, true)

    // Hide subscribe form
    this.els['subscribe-form'].element.setAttribute('aria-hidden', true)

    // Show registered text
    this.els['subscribed-form'].element.setAttribute('aria-hidden', false)
  }

  validEmail () {
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    return emailRegex.test(emailAddress)
  }
}

if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', NewsletterForm)
}
